import { z } from 'zod';
import { UserIdentity } from './common.js';
import { CredentialSource } from './team-ai.js';
/**
 * What the playground's AI spent, and on what (docs/playground-usage-plan.md).
 *
 * agentd reports one {@link UsageTurnReport} per turn to the Worker, which keeps it in
 * `playground_ai_turns`; the dashboard reads it back a game or a conversation at a time. The record
 * holds **facts** — what Codex reported for every model request, and what each request did — and
 * nothing that explains them. The explanation is {@link explainTurn}, computed at read time, so a
 * better one is a code change and never a migration (§0.2).
 *
 * A record carries nothing the team's transcript does not already show (§0.5): numbers, ids,
 * model names, and labels — the start of what was typed, command lines, `server.tool` names, file
 * paths, search queries. A command's output, a tool's result, the model's words and its reasoning
 * are only ever present as their *size*.
 */
/** How long a label may be: a command line or a list of paths, cut short. */
export const USAGE_LABEL_CHARS = 160;
/** How much of what was typed labels a turn — the same cut a history version's label makes. */
export const USAGE_PROMPT_LABEL_CHARS = 120;
/** A turn past either bound keeps its totals and says `truncated`. */
export const USAGE_MAX_REQUESTS = 400;
export const USAGE_MAX_ITEMS = 600;
/** Per call to the Worker. */
export const USAGE_REPORT_TURNS = 50;
export const USAGE_MISSING_KEYS = 5000;
/** The serialized `detail` of one turn, which the Worker refuses beyond. */
export const USAGE_DETAIL_BYTES = 128 * 1024;
/** How many conversations the list answers with at once. */
export const USAGE_SESSIONS_PAGE = 100;
/** How many turns one conversation answers with at once. */
export const USAGE_TURNS_PAGE = 200;
/**
 * The conversation release notes are filed under. Not a chat conversation — `@` is outside the
 * alphabet agentd makes conversation ids from, so it can never collide with one.
 */
export const NOTES_CONVERSATION = '@notes';
/** A chat conversation id as agentd makes them (`first`, or `newSessionId`), or the notes one. */
export const UsageConversation = z.string().regex(/^(@notes|[a-z0-9-]{1,64})$/);
const count = z.number().int().nonnegative();
/** A number of tokens that one source knows and another does not: `null` is "not reported". */
const known = count.nullable();
/**
 * Tokens, as Codex counts them. `cached` is the part of `input` served from the prompt cache and
 * `cacheWrite` the part written to it; `reasoning` is the part of `output` the model spent thinking.
 * The last two are `null` where the source never said — a turn read back from the transcript.
 */
export const UsageTokens = z.object({
    input: count,
    cached: count,
    cacheWrite: known,
    output: count,
    reasoning: known,
});
/** One model request: its own numbers (Codex's `last`), not the thread's running total. */
export const UsageRequest = z.object({
    at: count,
    input: count,
    cached: count,
    cacheWrite: count,
    output: count,
    reasoning: count,
    /** The model's context window, when Codex knew it. */
    window: known,
});
/**
 * What kind of thing the model did. `other` is an item type this version of agentd does not name —
 * counted, labelled with the type, never dropped.
 */
export const UsageItemKind = z.enum([
    'command',
    'edit',
    'mcp',
    'search',
    'image',
    'plan',
    'reasoning',
    'message',
    'compaction',
    'other',
]);
/** One thing the model did in a turn. */
export const UsageItem = z.object({
    kind: UsageItemKind,
    /** The command line, `server.tool`, the paths, the query. Empty for reasoning and replies. */
    label: z.string().max(USAGE_LABEL_CHARS),
    /** The request (1-based) whose output issued it; 0 when the source has no requests. */
    request: count,
    /** How many characters came back to the model — the size of the result, never the result. */
    resultChars: known,
    /** Images in what came back. */
    images: count,
    ms: known,
    failed: z.boolean(),
});
/** One of the plan's rolling windows, as Codex reports it. */
export const UsageLimitWindow = z.object({
    usedPercent: z.number().min(0).max(1000),
    windowMins: known,
    resetsAt: known,
});
export const UsageLimits = z.object({
    primary: UsageLimitWindow.nullable(),
    secondary: UsageLimitWindow.nullable(),
});
/** Everything about a turn beyond its totals. */
export const TurnUsageDetail = z.object({
    v: z.literal(1),
    requests: z.array(UsageRequest).max(USAGE_MAX_REQUESTS),
    items: z.array(UsageItem).max(USAGE_MAX_ITEMS),
    /** Where the plan's limits stood before the turn and after it, when Codex said. */
    limits: z.object({ before: UsageLimits.nullable(), after: UsageLimits.nullable() }).nullable(),
    /** A bound was reached: the lists are the first of their kind, and the totals are still whole. */
    truncated: z.boolean(),
});
/**
 * What started the turn. A `message` is anything the developer (or the dashboard on their behalf:
 * a survey, a rewrite) sent; `compact` and `wrapup` are agentd's own instructions; `notes` is a
 * deploy's release notes.
 */
export const UsageTurnKind = z.enum(['message', 'compact', 'wrapup', 'notes']);
/** `live` was reported as the turn ended; `transcript` was read back from a transcript later. */
export const UsageSource = z.enum(['live', 'transcript']);
export const UsageHarness = z.enum(['app-server', 'exec']);
export const UsageTurnStatus = z.enum(['completed', 'interrupted', 'failed']);
/** One turn, as agentd reports it. */
export const UsageTurnReport = z.object({
    conversation: UsageConversation,
    /** The `at` of the turn's `usage` line. With the conversation, the turn's key. */
    usageAt: count,
    kind: UsageTurnKind,
    source: UsageSource,
    harness: UsageHarness.nullable(),
    label: z.string().max(USAGE_LABEL_CHARS),
    /** How long the prompt that went to the model was, in characters — a size, not the text. */
    promptChars: known,
    model: z.string().max(80).nullable(),
    effort: z.string().max(24).nullable(),
    status: UsageTurnStatus.nullable(),
    startedAt: count,
    endedAt: known,
    totals: UsageTokens,
    /** How many model requests the turn made; 0 when the source cannot say. */
    requests: count,
    detail: TurnUsageDetail.nullable(),
    /**
     * What the turn ran on: the developer's ChatGPT, or the team's AI
     * (`docs/playground-team-ai-plan.md`). Absent from an older agentd, and from a backfill that
     * cannot tell, which both mean ChatGPT.
     */
    credential: CredentialSource.optional(),
});
/**
 * A turn's key: the conversation and the `at` of its `usage` line. A live report and a backfill of
 * the same turn arrive at the same key, because the live report is keyed by the very line the
 * backfill reads.
 */
export function usageTurnKey(conversation, usageAt) {
    return `${conversation}:${usageAt}`;
}
export const UsageReportRequest = z.object({
    turns: z.array(UsageTurnReport).min(1).max(USAGE_REPORT_TURNS),
});
export const UsageMissingRequest = z.object({
    keys: z.array(z.string().max(96)).max(USAGE_MISSING_KEYS),
});
export const UsageMissingResponse = z.object({ missing: z.array(z.string()) });
/* ------------------------------------------------------------------------------- reading */
/** Sums over turns. `cacheWrite` and `reasoning` add up only what was reported. */
export const UsageTotals = z.object({
    turns: count,
    requests: count,
    input: count,
    cached: count,
    cacheWrite: count,
    output: count,
    reasoning: count,
});
/** One session in the list: a chat conversation, or the game's release notes. */
export const UsageSessionSummary = z.object({
    conversation: UsageConversation,
    /** Its first message's label; `null` for a conversation with no message turn (a compaction). */
    title: z.string().nullable(),
    firstAt: count,
    lastAt: count,
    totals: UsageTotals,
    models: z.array(z.string()),
    /** Who started the sandboxes its live turns ran in. */
    people: z.array(UserIdentity),
    /** Whether any of its turns has per-request detail. */
    detailed: z.boolean(),
    /** The largest single request, and the window it was measured against. */
    peakContext: known,
    contextWindow: known,
});
export const PlaygroundUsage = z.object({
    totals: UsageTotals,
    byModel: z.array(z.object({ model: z.string().nullable(), totals: UsageTotals })),
    byPerson: z.array(z.object({ person: UserIdentity.nullable(), totals: UsageTotals })),
    sessions: z.array(UsageSessionSummary),
    /** There are older sessions than these; ask again with `before` = the last one's `lastAt`. */
    more: z.boolean(),
});
/** One turn, as the dashboard reads it back. */
export const UsageTurn = UsageTurnReport.omit({ conversation: true }).extend({
    id: z.string(),
    peakContext: known,
    contextWindow: known,
    /** Who started the sandbox a live turn ran in; `null` for a transcript turn, or a departed account. */
    startedBy: UserIdentity.nullable(),
});
export const PlaygroundUsageSession = z.object({
    session: UsageSessionSummary,
    turns: z.array(UsageTurn),
    /** Older turns exist beyond these; ask again with `before` = the first one's `usageAt`. */
    more: z.boolean(),
});
/** A character is about a quarter of a token in code and English. */
const CHARS_PER_TOKEN = 4;
/** What one image costs, flat: a screenshot's tiles at high detail. An estimate, and labelled one. */
export const USAGE_IMAGE_TOKENS = 765;
/** A tool result's framing — the call id, the wrapper — before any of its content. */
const FRAMING_TOKENS = 16;
/** The kinds that hand something back to the model — the ones a request's growth can be split among. */
const RETURNS = new Set(['command', 'edit', 'mcp', 'search', 'image', 'plan', 'other']);
/** The rolled-up name of each group, for the ones that are not an MCP server. */
export const USAGE_GROUP_LABELS = {
    history: 'Already in the conversation',
    prompt: 'Your messages',
    assistant: 'Reasoning and tool calls, carried forward',
    command: 'Command output',
    edit: 'File edits',
    search: 'Web search',
    image: 'Images',
    plan: 'Plans',
    compaction: 'Compacted context',
    other: 'Other tools',
    unattributed: 'Not attributed',
};
function groupOf(category, label) {
    if (category !== 'mcp')
        return category;
    const server = label.split('.')[0] || 'mcp';
    return `mcp:${server}`;
}
/** What a group is called: its label, or `<server> tools`. */
export function usageGroupLabel(group) {
    if (group.startsWith('mcp:'))
        return `${group.slice(4)} tools`;
    return USAGE_GROUP_LABELS[group] ?? group;
}
function weight(item) {
    return (item.resultChars ?? 0) / CHARS_PER_TOKEN + item.images * USAGE_IMAGE_TOKENS + FRAMING_TOKENS;
}
/** `total` divided by `weights`, in whole tokens that add up to exactly `total`. */
function apportion(total, weights) {
    const sum = weights.reduce((a, b) => a + b, 0);
    if (sum <= 0)
        return weights.map(() => 0);
    const raw = weights.map((w) => (total * w) / sum);
    const shares = raw.map(Math.floor);
    let left = total - shares.reduce((a, b) => a + b, 0);
    const order = raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0]);
    for (const [, i] of order) {
        if (left <= 0)
            break;
        shares[i] = shares[i] + 1;
        left -= 1;
    }
    return shares;
}
function categoryOf(kind) {
    switch (kind) {
        case 'command':
        case 'edit':
        case 'mcp':
        case 'search':
        case 'image':
        case 'plan':
            return kind;
        default:
            return 'other';
    }
}
/**
 * Split each of a turn's requests into the pieces of context it was made of (plan §3).
 *
 * Every request re-sends everything before it, so the turn's input is a sum over requests of a
 * context that grows. Request 1 is what was already in the thread plus the prompt; every later
 * request is the one before plus its growth, which is the previous request's own output carried
 * forward and then the results of what that request issued, divided by their size. A request whose
 * input shrank is a compaction, and the context restarts there.
 *
 * The request totals are exact and the pieces always add up to them: `Σ (added + reread)` over the
 * pieces is `Σ input` over the requests. How a growth divides between two things that fed it at
 * once is an estimate.
 */
export function explainTurn(turn) {
    const requests = turn.detail?.requests ?? [];
    const items = turn.detail?.items ?? [];
    const output = {
        reasoning: requests.reduce((sum, r) => sum + Math.min(r.reasoning, r.output), 0),
        replies: requests.reduce((sum, r) => sum + r.output - Math.min(r.reasoning, r.output), 0),
    };
    if (requests.length === 0)
        return { split: false, pieces: [], output };
    // Each piece with the request it entered at (0-based) and the one it stopped being sent at.
    const open = [];
    const add = (category, label, size, from) => {
        if (size <= 0)
            return;
        open.push({ piece: { category, group: groupOf(category, label), label, request: from + 1, added: size }, from, until: requests.length });
    };
    const first = requests[0];
    const prompt = Math.min(first.input, Math.round((turn.promptChars ?? 0) / CHARS_PER_TOKEN));
    add('history', USAGE_GROUP_LABELS.history, first.input - prompt, 0);
    add('prompt', 'Your message', prompt, 0);
    for (let k = 1; k < requests.length; k++) {
        const request = requests[k];
        const before = requests[k - 1];
        const growth = request.input - before.input;
        if (growth < 0) {
            for (const entry of open)
                if (entry.until > k)
                    entry.until = k;
            add('compaction', USAGE_GROUP_LABELS.compaction, request.input, k);
            continue;
        }
        const carried = Math.min(before.output, growth);
        add('assistant', `Request ${k}'s reasoning and tool calls`, carried, k);
        const rest = growth - carried;
        if (rest <= 0)
            continue;
        // Items are numbered by the request that issued them, 1-based: request `k` (1-based) is index k-1.
        const fed = items.filter((item) => item.request === k && RETURNS.has(item.kind));
        if (fed.length === 0) {
            add('unattributed', USAGE_GROUP_LABELS.unattributed, rest, k);
            continue;
        }
        const shares = apportion(rest, fed.map(weight));
        fed.forEach((item, i) => add(categoryOf(item.kind), item.label || item.kind, shares[i] ?? 0, k));
    }
    const pieces = open.map(({ piece, from, until }) => ({ ...piece, reread: piece.added * Math.max(0, until - from - 1) }));
    return { split: true, pieces, output };
}
/** Sum pieces by group, largest first. Several turns' pieces sum the same way one turn's do. */
export function rollUpPieces(pieces) {
    const groups = new Map();
    for (const piece of pieces) {
        const share = groups.get(piece.group) ?? { group: piece.group, label: usageGroupLabel(piece.group), added: 0, reread: 0 };
        share.added += piece.added;
        share.reread += piece.reread;
        groups.set(piece.group, share);
    }
    return [...groups.values()].sort((a, b) => b.added + b.reread - (a.added + a.reread));
}
//# sourceMappingURL=playground-usage.js.map